import { memo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AltRouteRounded,
    AutoAwesomeRounded,
    CheckRounded,
    KeyboardArrowDownRounded,
    SvgIconComponent,
} from '@mui/icons-material';
import { Box, ButtonBase, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, alpha } from '@mui/material';
import { useAndieChatControls } from '@/providers/andie-chat-provider';

type AndieChatMode = 'auto' | 'guided';

type ModeOption = {
    value: AndieChatMode;
    key: 'auto' | 'guided';
    Icon: SvgIconComponent;
    label: string;
    description: string;
};

const MODE_OPTIONS: ModeOption[] = [
    {
        value: 'auto',
        key: 'auto',
        Icon: AutoAwesomeRounded,
        label: 'Auto',
        description: 'Andie plans the steps and runs them end to end.',
    },
    {
        value: 'guided',
        key: 'guided',
        Icon: AltRouteRounded,
        label: 'Guided',
        description: 'Andie plans the steps and checks in before key moves.',
    },
];

// Main chat is always harnessed: anything that is not explicitly 'guided' defaults to 'auto'.
const normalizeMode = (mode: string | null): AndieChatMode => (mode === 'guided' ? 'guided' : 'auto');

export const AndieChatModeSelector = memo(() => {
    const { t } = useTranslation();
    const { mode, setMode, isLoading, isHydrated } = useAndieChatControls();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const open = Boolean(anchorEl);
    const disabled = isLoading || !isHydrated;

    const currentValue = normalizeMode(mode);
    const current = MODE_OPTIONS.find((option) => option.value === currentValue) ?? MODE_OPTIONS[0];
    const active = true;

    const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleSelect = useCallback(
        (value: AndieChatMode) => {
            setMode(value);
            setAnchorEl(null);
        },
        [setMode],
    );

    return (
        <>
            <Tooltip title={t('andie.chat.mode.tooltip', { defaultValue: 'Response mode' })}>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                    <ButtonBase
                        focusRipple
                        disabled={disabled}
                        onClick={handleOpen}
                        aria-haspopup="menu"
                        aria-expanded={open}
                        aria-label={t('andie.chat.mode.aria', { defaultValue: 'Select Andie response mode' })}
                        sx={(theme) => ({
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            pl: 0.875,
                            pr: 0.625,
                            py: 0.5,
                            borderRadius: 2,
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            lineHeight: 1,
                            letterSpacing: 0,
                            color: active ? theme.palette.andie.main : theme.palette.text.secondary,
                            backgroundColor: active ? alpha(theme.palette.andie.main, 0.1) : 'transparent',
                            border: `1px solid ${
                                active ? alpha(theme.palette.andie.main, 0.24) : 'transparent'
                            }`,
                            transition: theme.transitions.create(
                                ['background-color', 'color', 'border-color'],
                                { duration: theme.transitions.duration.shorter },
                            ),
                            '&:hover': {
                                backgroundColor: active
                                    ? alpha(theme.palette.andie.main, 0.16)
                                    : theme.palette.action.hover,
                            },
                            '&.Mui-focusVisible': {
                                outline: `2px solid ${alpha(theme.palette.andie.main, 0.5)}`,
                                outlineOffset: 2,
                            },
                            '&.Mui-disabled': {
                                opacity: 0.45,
                            },
                        })}
                    >
                        <current.Icon sx={{ fontSize: 18 }} />
                        <Box component="span">
                            {t(`andie.chat.mode.${current.key}.label`, { defaultValue: current.label })}
                        </Box>
                        <KeyboardArrowDownRounded
                            sx={{
                                fontSize: 18,
                                transition: 'transform 180ms ease',
                                transform: open ? 'rotate(180deg)' : 'none',
                                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                            }}
                        />
                    </ButtonBase>
                </Box>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: { minWidth: 292, maxWidth: 320, borderRadius: 2, mt: -0.5 },
                    },
                    list: {
                        'aria-label': t('andie.chat.mode.aria', {
                            defaultValue: 'Select Andie response mode',
                        }),
                        dense: false,
                    },
                }}
            >
                {MODE_OPTIONS.map((option) => {
                    const selected = option.value === current.value;
                    return (
                        <MenuItem
                            key={option.key}
                            selected={selected}
                            onClick={() => handleSelect(option.value)}
                            sx={{ alignItems: 'flex-start', gap: 0.5, py: 1, whiteSpace: 'normal' }}
                        >
                            <ListItemIcon
                                sx={{ minWidth: 32, mt: 0.25, color: selected ? 'andie.main' : 'text.secondary' }}
                            >
                                <option.Icon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={t(`andie.chat.mode.${option.key}.label`, { defaultValue: option.label })}
                                secondary={t(`andie.chat.mode.${option.key}.description`, {
                                    defaultValue: option.description,
                                })}
                                slotProps={{
                                    primary: {
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        color: selected ? 'andie.main' : 'text.primary',
                                    },
                                    secondary: { fontSize: '0.75rem', color: 'text.secondary' },
                                }}
                            />
                            {selected && (
                                <CheckRounded sx={{ fontSize: 18, mt: 0.375, color: 'andie.main', flexShrink: 0 }} />
                            )}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
});

AndieChatModeSelector.displayName = 'AndieChatModeSelector';
